import React from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import Container from '../../components/shared/atoms/Container';
import Box from '../../components/shared/atoms/Box';
import Heading from '../../components/shared/atoms/Heading';
import Text from '../../components/shared/atoms/Text';
import Image from '../../components/shared/atoms/Image';
import Separator from '../../components/Separator';
import CityCard from '../../components/annuaire/CityCard';
import Row from '../../components/shared/atoms/Row';

// Styled component for letter links
const LetterLink = styled(Link)`
  color: black;
  margin: 0 8px;
  text-decoration: none;
  &:after {
    content: '-';
    margin-left: 8px;
  }
  &:last-child:after {
    content: '';
  }
`;

const AnnuaireHome = () => {
  const { t } = useTranslation();
  return (
    <Container fluid>
      <Helmet
        title="Annuaire des réparateurs vélo en France | Roulez Jeunesse"
        meta={[
          {
            name: 'description',
            content:
              'Trouvez un réparateur vélo près de chez vous dans toute la France.',
          },
          { name: 'twitter:card', content: 'summary_large_image' },
          {
            name: 'twitter:title',
            content:
              'Annuaire des réparateurs vélo en France | Roulez Jeunesse',
          },
          {
            name: 'twitter:description',
            content:
              'Trouvez un réparateur vélo près de chez vous dans toute la France.',
          },
          {
            name: 'twitter:image',
            content:
              'https://uploads-ssl.webflow.com/664e20aae5145eb7a61de885/666854745cce5ad6c682189f_Re%CC%81parer%20son%20ve%CC%81lo%20devient%20un%20jeu%20d%E2%80%99enfant.png',
          },
        ]}
      />
      <Container mb={11}>
        <Heading mt={4}>{t('homedirectory.heading')}</Heading>
        <Text variant="large" mt={-4} mb={5}>
          {t('homedirectory.text')}
        </Text>
        <Image
          src="/static/img/annuaire/hero-annuaire.jpg"
          alt="Annuaire des réparateurs vélo en France - Roulez Jeunesse"
          mb={{ xs: 7, md: 10 }}
          style={{ borderRadius: 16, minHeight: 150 }}
        />
        <Box flexDirection="row">
          <Heading level={4} mr={2}>
            {t('homedirectory.title')}
          </Heading>
          <Box flex={1}>
            <Separator axe="horizontal" />
          </Box>
        </Box>
        <Row gap={24} sm mb={4}>
          <CityCard
            city="montpellier"
            dept="herault"
            label="Montpellier"
            url="/static/img/annuaire/montpellier.jpg"
            alt="Réparateur vélo à Montpellier"
            nombreAtelier={10}
          />
          <CityCard
            city="reims"
            dept="marne"
            label="Reims"
            url="/static/img/annuaire/reims.jpg"
            alt="Réparateur vélo à Reims"
            nombreAtelier={4}
          />
          <CityCard
            city="toulon"
            dept="var"
            label="Toulon"
            url="/static/img/annuaire/toulon.jpg"
            alt="Réparateur vélo à Toulon"
            nombreAtelier={6}
          />
          <CityCard
            city="saint-etienne"
            dept="loire"
            label="Saint-Etienne"
            url="/static/img/annuaire/saint-etienne.jpg"
            alt="Réparateur vélo à Saint-Étienne"
            nombreAtelier={7}
          />
        </Row>
        <Row gap={24} sm mb={4}>
          <CityCard
            city="le-havre"
            dept="seine-maritime"
            label="Le Havre"
            url="/static/img/annuaire/le-havre.jpg"
            alt="Réparateur vélo au Havre"
            nombreAtelier={6}
          />
          <CityCard
            city="grenoble"
            dept="isere"
            label="Grenoble"
            url="/static/img/annuaire/grenoble.jpg"
            alt="Réparateur vélo à Grenoble"
            nombreAtelier={19}
          />
          <CityCard
            city="dijon"
            dept="cote-dor"
            label="Dijon"
            url="/static/img/annuaire/dijon.jpg"
            alt="Réparateur vélo à Dijon"
            nombreAtelier={5}
          />
          <CityCard
            city="angers"
            dept="maine-et-loire"
            label="Angers"
            url="/static/img/annuaire/angers.jpg"
            alt="Réparateur vélo à Angers"
            nombreAtelier={9}
          />
        </Row>
        <Row gap={24} sm>
          <CityCard
            city="nimes"
            dept="gard"
            label="Nimes"
            url="/static/img/annuaire/nimes.jpg"
            alt="Réparateur vélo à Nîmes"
            nombreAtelier={11}
          />
          <CityCard
            city="clermont-ferrand"
            dept="puy-de-dome"
            label="Clermont-Ferrand"
            url="/static/img/annuaire/clermont-ferrand.jpg"
            alt="Réparateur vélo à Clermont-Ferrand"
            nombreAtelier={6}
          />
          <CityCard
            city="aix-en-provence"
            dept="bouches-du-rhone"
            label="Aix-en-Provence"
            url="/static/img/annuaire/aix-en-provence.jpg"
            alt="Réparateur vélo à Aix-en-Provence"
            nombreAtelier={14}
          />
          <CityCard
            city="le-mans"
            dept="sarthe"
            label="Le Mans"
            url="/static/img/annuaire/le-mans.jpg"
            alt="Réparateur vélo au Mans"
            nombreAtelier={11}
          />
        </Row>
        <Box my={4} flexDirection="row" justifyContent="center" flexWrap="wrap">
          {Array.from(Array(26)).map((_, index) => {
            const letter = String.fromCharCode(65 + index); // Generates letters A-Z
            return (
              <LetterLink
                key={letter}
                to={`/annuaire-velo/letter/${letter.toLowerCase()}`}>
                {letter}
              </LetterLink>
            );
          })}
        </Box>
      </Container>
    </Container>
  );
};

export default AnnuaireHome;
